import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Plus, Trash2, Edit2, Copy, Check, X, Search,
  Tag, Clock, Users, ChevronDown, Loader2, Star, Layout
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { FIELD_TYPES } from './FormFieldBuilder';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category badges
const CATEGORIES = {
  conference: { label: 'Conference', color: 'bg-purple-100 text-purple-700' },
  workshop: { label: 'Workshop', color: 'bg-blue-100 text-blue-700' },
  tournament: { label: 'Tournament', color: 'bg-green-100 text-green-700' },
  general: { label: 'General', color: 'bg-gray-100 text-gray-700' }
};

// Template Card Component
const TemplateCard = ({ template, onSelect, onEdit, onDelete, selected }) => {
  const category = CATEGORIES[template.category] || CATEGORIES.general;
  const fields = template.fields_config || [];

  return (
    <div 
      className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-md ${
        selected ? 'border-green-500 ring-2 ring-green-500/20' : 'border-gray-200 hover:border-green-300'
      }`}
      onClick={() => onSelect(template)}
      data-testid={`template-card-${template.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-900">{template.name}</h3>
          <p className="text-sm text-gray-500 line-clamp-2">{template.description}</p>
        </div>
        {selected && (
          <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
            <Check className="w-4 h-4 text-white" />
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <span className={`text-xs px-2 py-1 rounded ${category.color}`}>
          {category.label}
        </span>
        {template.owner_type === 'system' && (
          <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-700">
            System
          </span>
        )}
        <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">
          {fields.length} fields
        </span>
      </div>

      <div className="flex flex-wrap gap-1">
        {fields.slice(0, 4).map((field, idx) => (
          <span key={idx} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
            {field.field_label}
          </span>
        ))}
        {fields.length > 4 && (
          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded">
            +{fields.length - 4} more
          </span>
        )}
      </div>

      {/* Actions */}
      {template.owner_type !== 'system' && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(template); }}
            className="p-1.5 hover:bg-gray-100 rounded text-gray-500"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(template); }}
            className="p-1.5 hover:bg-red-50 rounded text-red-500"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

// Save as Template Modal
const SaveTemplateModal = ({ isOpen, onClose, onSave, fields, existingTemplate }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (existingTemplate) {
      setName(existingTemplate.name);
      setDescription(existingTemplate.description || '');
      setCategory(existingTemplate.category || 'general');
      setIsPublic(existingTemplate.is_public || false);
    } else {
      setName('');
      setDescription('');
      setCategory('general');
      setIsPublic(false);
    }
  }, [existingTemplate, isOpen]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        category,
        is_public: isPublic,
        fields_config: fields
      });
      onClose();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl w-full max-w-md shadow-2xl"
      >
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {existingTemplate ? 'Update Template' : 'Save as Template'}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Save this field configuration for reuse in other events
          </p>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500"
              placeholder="e.g., Corporate Conference Registration"
              data-testid="template-name-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500"
              placeholder="Describe when to use this template..."
              rows={2}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500"
            >
              {Object.entries(CATEGORIES).map(([key, cat]) => (
                <option key={key} value={key}>{cat.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isPublic"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="w-4 h-4 text-green-600 rounded"
            />
            <label htmlFor="isPublic" className="text-sm text-gray-700">
              Make this template available to all users
            </label>
          </div>

          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm text-gray-600">
              <strong>{fields.length} fields</strong> will be saved to this template
            </p>
          </div>
        </div>

        <div className="p-6 border-t flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-template-btn"
          >
            {saving ? 'Saving...' : existingTemplate ? 'Update' : 'Save Template'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// Template Selector Component (for use in Event Wizard)
const TemplateSelector = ({ onSelect, currentFields }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ is_public: 'true' });
      if (categoryFilter) params.append('category', categoryFilter);
      
      const res = await fetch(`${API}/universal-events/templates?${params}`);
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSelect = (template) => {
    setSelectedId(template.id);
    if (onSelect) {
      onSelect(template.fields_config);
    }
  };

  const handleSaveTemplate = async (data) => {
    const res = await fetch(`${API}/universal-events/templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    
    if (res.ok) {
      toast.success('Template saved successfully');
      fetchTemplates();
    } else {
      throw new Error('Failed to save');
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!confirm(`Delete template "${template.name}"?`)) return;
    
    try {
      const res = await fetch(`${API}/universal-events/templates/${template.id}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Template deleted');
        fetchTemplates();
      } else {
        toast.error('Failed to delete template');
      }
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="template-selector">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Form Templates</h3>
          <p className="text-sm text-gray-500">Select a template or save your current configuration</p>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowSaveModal(true)}
          disabled={!currentFields || currentFields.length === 0}
        >
          <Plus className="w-4 h-4 mr-2" />
          Save as Template
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          />
        </div>
        
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500"
        >
          <option value="">All Categories</option>
          {Object.entries(CATEGORIES).map(([key, cat]) => (
            <option key={key} value={key}>{cat.label}</option>
          ))}
        </select>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">Loading templates...</p>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-xl">
          <Layout className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No templates found</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              selected={selectedId === template.id}
              onSelect={handleSelect}
              onEdit={() => {}}
              onDelete={handleDeleteTemplate}
            />
          ))}
        </div>
      )}

      {/* Save Template Modal */}
      <SaveTemplateModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveTemplate}
        fields={currentFields || []}
      />
    </div>
  );
};

export default TemplateSelector;
export { SaveTemplateModal, TemplateCard, CATEGORIES };
